import { PINROLES, ROLES } from '@/utils/constants';
import { AxiosInstance } from 'axios';
import { GetterTree } from 'vuex';
import { AuthState, RootState } from '../types';
import { PERMISSION, hasPermission as resolvePermission } from '@/permissions';
import { getCountryFromAddress, getCurrencyFromCountry, resolvePriceAffixes } from '@/utils/currency';
import { isPizzabotIntegration, isPizzabotV2Integration } from '@/utils/general';
import i18n from '@/i18n';

export const getters: GetterTree<AuthState, RootState> = {
	/**
	 * Returns a predicate that resolves whether the current user's role may use a feature,
	 * accounting for POS integration. Mirror of the matrix; see src/permissions.
	 *
	 * @param {AuthState} _state - auth state (unused; role/POS read via getters)
	 * @param {object} authGetters - the auth module getters
	 * @return {(permission: PERMISSION) => boolean} permission predicate
	 */
	hasPermission(_state: AuthState, authGetters): (permission: PERMISSION) => boolean {
		return (permission: PERMISSION): boolean =>
			resolvePermission(permission, authGetters.getRoleLevel, authGetters.isPosIntegrated);
	},

	getTenantId(state: AuthState): string | null {
		return state.tenantId;
	},

	getAxiosInst(state: AuthState): AxiosInstance {
		return state.axiosInst;
	},

	getRestaurants(state: AuthState): Tenant[] | null {
		return state.restaurants;
	},

	getAuthGroups(state: AuthState): string[] {
		return state.authGroups || [];
	},

	getRestaurant(state: AuthState): Restaurant | object {
		return state.restaurant;
	},

	getCountry(state: AuthState): string | null {
		return getCountryFromAddress(state.restaurant?.address);
	},

	getCurrency(_state: AuthState, authGetters): string {
		return getCurrencyFromCountry(authGetters.getCountry);
	},

	getPriceAffixes(_state: AuthState, authGetters): PriceAffixes {
		// Currency symbol and position for price input fields.
		// Matches the logic of `$n(value, 'currency')`.
		return resolvePriceAffixes(authGetters.getCurrency, i18n.locale);
	},

	getCurrencySymbol(_state: AuthState, authGetters): string {
		const affixes: PriceAffixes = authGetters.getPriceAffixes;
		return affixes.prefix || affixes.suffix;
	},

	isSwitchingLocation(state: AuthState): boolean {
		return state.switchingLocation;
	},

	getRestaurantById(state): Tenant | object | null {
		return (id: string) => {
			if(state.restaurants) {
				return state.restaurants.find((restaurant: Tenant) => restaurant.id === id);
			}
			return null;
		};
	},

	getRoleLevel(state: AuthState): number {
		if (!state.role || (ROLES[state.role] === undefined || ROLES[state.role] === null)) {
			return ROLES.member;
		}
		else {
			return ROLES[state.role];
		}
	},

	getPinRole(state: AuthState): number {
		if (!state.pinRole || (PINROLES[state.pinRole] === undefined || PINROLES[state.pinRole] === null)) {
			return PINROLES.admin; // assume highest level of access
		}
		else {
			return PINROLES[state.pinRole];
		}
	},


	isAuthenticated(state: AuthState): boolean {
		return state.authenticated;
	},

	getStaffEmail(state: AuthState): string | null {
		return state.staffEmail;
	},

	getEmployeeId(state: AuthState): string | null {
		return state.employeeId;
	},

	getEmployeeName(state: AuthState): string | null {
		return state.employeeName;
	},

	getCreatedBy(state: AuthState): string | null {
		return state.createdBy;
	},

	isPosIntegrated(state: AuthState): boolean {
		return state.posIntegrated;
	},

	isMarketplaceEnabled(state: AuthState): boolean {
		return state.restaurant.marketplace_hub;
	},

	isOrderManagementEnabled(state: AuthState): boolean {
		return state.restaurant.order_management;
	},

	areSuitesEnabled(state: AuthState): boolean {
		return state.restaurant.suites_enabled;
	},

	isCateringRestaurant(state: AuthState): boolean {
		return state.restaurant.catering;
	},

	isTaxInclusiveWithInfo(state: AuthState): boolean {
		return state.restaurant.tax_inclusive === 'tax_inclusive_with_info';
	},

	getOrderOptions(state:AuthState): OrderOptions {
		return state.restaurant.order_options || {};
	},

	getCheckinQuestion(state: AuthState, _getters, rootState: RootState): CheckinQuestion {
		const mappedItems = state.restaurant.menu_manager_config?.checkin_question?.mapped_items || [];
		const menus = rootState?.menus?.publishedMenus || [];

		// Only enabled for Bowlero POS-integrated locations with menus loaded
		// TODO: Remove the restaurant.group check when all Bowlero locations are ready to use restaurant.groups
		if (!(state.restaurant.group === 'bowlero' || state.restaurant.groups?.includes('bowlero')) || !state.posIntegrated || !menus.length) {
			return { mapped_items: [] };
		}

		// Collect all available menu items (not sold out, unavailable, or with options/addons)
		// Filter menus by order type to avoid SKU collisions between event and retail items
		const isEventOrder = rootState?.orders?.orderDetails?.menuManagerEvent === true;
		const availableMenuItems = menus.filter((menu: Menu) => menu.event_package === isEventOrder).flatMap((menu: Menu) => [
			...(menu.items || []),
			...(menu.sections || []).flatMap((s: MenuSection) => s.items || [])
		]).filter((item: MenuItem) =>
			!item.sold_out &&
			!item.unavailable &&
			!item.options?.length &&
			!item.addons?.length
		);

		// Create a map of available SKUs
		const itemsBySku = new Map(
			availableMenuItems.map((item: MenuItem) => [item.sku, item])
		);

		// Filter mapped items to only include available items
		const validMappedItems = mappedItems
			.filter((item: CheckinMappedItem) => item.item_sku && itemsBySku.has(item.item_sku))
			.map((item: CheckinMappedItem) => ({
				...item,
				menu_item: itemsBySku.get(item.item_sku!)
			}));

		return { mapped_items: validMappedItems };
	},

	getMaxItemsPerCart(state: AuthState): number | undefined {
		return state.restaurant.order_options?.max_items_per_cart;
	},

	getMaxMealsPerCart(state: AuthState): number | undefined {
		return state.restaurant.order_options?.max_meals_per_cart;
	},

	getRestaurantDeliveryInfo(state: AuthState): Delivery | null {
		return state.restaurant.delivery;
	},

	areNotificationSoundsAllowed(state: AuthState): boolean {
		return state.restaurant.menu_manager_config?.allow_order_manager_sound ?? false;
	},

	allowBulkComplete(state: AuthState): boolean {
		return state.restaurant.menu_manager_config?.allow_bulk_complete ?? false;
	},

	allowBulkCancel(state: AuthState): boolean {
		return state.restaurant.menu_manager_config?.allow_bulk_cancel ?? false;
	},

	isCsvImportEnabled(state: AuthState): boolean {
		return state.restaurant.menu_manager_config?.csv_import ?? false;
	},

	getContactFieldsConfig(state: AuthState): ContactFieldsConfig {
		const contactFieldsConfig: ContactFieldsConfig | undefined = state.restaurant.menu_manager_config?.contact_fields_config;

		// If the config is not set, return the default settings (all fields required and not hidden)
		if (!contactFieldsConfig || !Object.keys(contactFieldsConfig).length) {
			return {
				name: { required: true, hide: false },
				email: { required: true, hide: false },
				phone_number: { required: true, hide: false }
			};
		}
		return {
			name: {
				required: contactFieldsConfig?.name?.required ?? true,
				hide: contactFieldsConfig?.name?.hide ?? false
			},
			email: {
				required: contactFieldsConfig?.email?.required ?? true,
				hide: contactFieldsConfig?.email?.hide ?? false
			},
			phone_number: {
				required: contactFieldsConfig?.phone_number?.required ?? true,
				hide: contactFieldsConfig?.phone_number?.hide ?? false
			}
		};
	},

	isK12Location(state: AuthState): boolean {
		return state.restaurant.k12;
	},

	hasSubsidies(state: AuthState): boolean {
		return state.restaurant.subsidies;
	},

	areApp8CreditsEnabled(state: AuthState): boolean {
		return state.restaurant.app8_credits;
	},

	isRallyIntelligenceEnabled(state: AuthState): boolean {
		if (state.tenantId !== state.restaurant?.id) {
			return false;
		}
		return state.restaurant.rally_intelligence;
	},

	getTimezone(state: AuthState): string {
		return state.restaurant.menu_configuration?.zone || 'America/Toronto';
	},

	getMembershipPrograms(state: AuthState): string[] | undefined {
		return state.restaurant.membership_programs;
	},

	hideItemsSpecialInstructions(state): boolean {
		return state.restaurant && state.restaurant.hide_items_special_instructions;
	},

	hideTakeoutSpecialInstructions(state): boolean {
		return state.restaurant && state.restaurant.hide_takeout_special_instructions;
	},

	getMembershipCustomization(state: AuthState): MembershipCustomization | undefined {
		return state.restaurant.menu_configuration?.membership_customization;
	},

	getDiscounts(state: AuthState): DiscountInfo {
		return state.restaurant.discount || {};
	},

	getHolidayHours(state: AuthState): HolidayHours[] {
		return state.restaurant.holiday_hours || [];
	},

	areOrdersPaused(state: AuthState): boolean {
		return state.restaurant.orders_paused;
	},

	getExtraPaymentMethods(state: AuthState): RestaurantExtraPaymentMethod[] | null {
		return state.restaurant.extra_payment_methods;
	},

	hasExtraPaymentMethodsConfigured(state: AuthState): boolean {
		return state.restaurant.extra_payment_methods?.some(method => ['invoice', 'cost_center', 'purchase_order'].includes(method.type));
	},

	showReportingCategory(state: AuthState): boolean {
		// Defaults to true for backwards compatibility, if location is not integrated
		return !state.restaurant.pos_integrated && (state.restaurant.menu_manager_config?.reporting_categories ?? true);
	},

	isDashboardEnabled(state: AuthState): boolean {
		return state.restaurant.dashboard;
	},

	modifyRequestsEnabled(state: AuthState): boolean {
		return state.restaurant.modify_order_requests;
	},

	hideTakeoutTime(state: AuthState): boolean {
		return state.restaurant.order_options?.hide_takeout_time || false;
	},

	isGuestOrderCancellationEnabled(state: AuthState): boolean {
		return !!state.restaurant.guest_order_cancellation;
	},

	isPizzabotLocation(state: AuthState): boolean {
		return isPizzabotIntegration(state.restaurant.integration_type);
	},

	isPizzabotV2Location(state: AuthState): boolean {
		return isPizzabotV2Integration(state.restaurant.integration_type);
	},

	areCateringEventsEnabled(state: AuthState): boolean {
		return !!(state.restaurant.catering && state.restaurant.catering_events);
	}
};